import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Building2, FileText, Layers, Activity, LogOut, ArrowLeft, ArrowRight,
  Check, Users, Wallet, Save, Pencil, UserCog, Send, Plus, X, MessagesSquare, ExternalLink, Paperclip,
  FileSignature, Copy, Download, Trash2, Globe, MapPin, Star, Phone, Mail, Tag,
  CopyPlus, DollarSign, Loader2, Inbox, Reply, RefreshCw, UserPlus, PenSquare,
} from "lucide-react";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import { getServiceIcon, getAccent } from "@/lib/serviceIcons";
import ProposalBuilder from "@/components/app/ProposalBuilder";
import { Copilot } from "@/components/app/Copilot";
import ProposalPaymentModal from "@/components/app/ProposalPaymentModal";
import WorkspaceModules from "@/components/app/WorkspaceModules";
import ContactModal from "@/components/app/ContactModal";
import ContactsManager from "@/components/app/ContactsManager";
import ProspectingAdmin from "@/components/app/ProspectingAdmin";
import { toast } from "sonner";

const BIZ_STATUS = {
  lead: "bg-blue-500/10 text-blue-400 border-blue-500/20",
  active: "bg-green-500/10 text-green-400 border-green-500/20",
  paused: "bg-amber-500/10 text-amber-400 border-amber-500/20",
  churned: "bg-red-500/10 text-red-400 border-red-500/20",
};

const extractEmail = (s) => { const m = /<([^>]+)>/.exec(s || ""); return m ? m[1] : (s || ""); };

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div className="glass-panel p-6 rounded-2xl flex items-center gap-4">
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center border ${color}`}><Icon size={22} /></div>
      <div>
        <div className="text-3xl font-bold text-white">{value}</div>
        <div className="text-xs text-zinc-500 font-bold uppercase tracking-wider">{label}</div>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { profile, loading, logout } = useApp();
  const navigate = useNavigate();
  const [tab, setTab] = useState("businesses");
  const [stats, setStats] = useState(null);
  const [businesses, setBusinesses] = useState([]);
  const [requests, setRequests] = useState([]);
  const [services, setServices] = useState([]);
  const [detail, setDetail] = useState(null);
  const [editing, setEditing] = useState({});
  const [mgrForm, setMgrForm] = useState(null);
  const [reply, setReply] = useState("");
  const [showLead, setShowLead] = useState(false);
  const [leadForm, setLeadForm] = useState({ name: "", business: "", email: "", phone: "", source: "DONDRÁE Sourced", price: 45 });
  const [wsPanel, setWsPanel] = useState(null);
  const [wsMsg, setWsMsg] = useState("");
  const [delivForm, setDelivForm] = useState({ title: "", url: "", note: "" });
  const [wsInvoice, setWsInvoice] = useState(null);
  const [itemForm, setItemForm] = useState({ description: "", amount: "", credit: false });
  const [planCount, setPlanCount] = useState(3);
  const [proposals, setProposals] = useState([]);
  const [showBuilder, setShowBuilder] = useState(false);
  const [editingProposal, setEditingProposal] = useState(null);
  const [builderMode, setBuilderMode] = useState(null); // 'engagement' = mark accepted on save (skip proposal phase)
  const [payModal, setPayModal] = useState(null); // proposal being managed (payments/accept) or null
  const [bizEdit, setBizEdit] = useState(null); // business edit form or null
  const [contactModal, setContactModal] = useState(null); // {contact} or {contact:null} for new
  const [showAddBiz, setShowAddBiz] = useState(false);
  const [bizForm, setBizForm] = useState({ business_name: "", contact_name: "", email: "", phone: "", industry: "", address: "", status: "active" });
  const [creatingBiz, setCreatingBiz] = useState(false);
  const [inbox, setInbox] = useState([]);
  const [inboxUnread, setInboxUnread] = useState(0);
  const [inboxMsg, setInboxMsg] = useState(null);
  const [inboxReply, setInboxReply] = useState("");
  const [sendingReply, setSendingReply] = useState(false);
  const [refreshingInbox, setRefreshingInbox] = useState(false);
  const [showCompose, setShowCompose] = useState(false);
  const [composeForm, setComposeForm] = useState({ to: "", subject: "", body: "", sender: "hello" });
  const [sendingCompose, setSendingCompose] = useState(false);

  useEffect(() => {
    if (!loading && (!profile || profile.role !== "admin")) navigate("/admin");
  }, [loading, profile, navigate]);

  const loadAll = async () => {
    try {
      const [s, b, r, sv, pr, ib] = await Promise.all([
        api.adminStats(), api.adminBusinesses(), api.adminRequests(), api.getServices(), api.listProposals(),
        api.listInbox().catch(() => ({ messages: [], unread: 0 })),
      ]);
      setStats(s); setBusinesses(b); setRequests(r); setServices(sv); setProposals(pr);
      setInbox(ib.messages || []); setInboxUnread(ib.unread || 0);
    } catch { /* handled by redirect */ }
  };

  const loadInbox = async () => {
    try { const ib = await api.listInbox(); setInbox(ib.messages || []); setInboxUnread(ib.unread || 0); } catch { /* ignore */ }
  };
  const refreshInbox = async () => {
    setRefreshingInbox(true);
    try { const ib = await api.listInbox(); setInbox(ib.messages || []); setInboxUnread(ib.unread || 0); toast.success("Inbox refreshed"); }
    catch { toast.error("Could not refresh inbox"); }
    finally { setRefreshingInbox(false); }
  };
  const sendCompose = async () => {
    if (!composeForm.to.trim() || !composeForm.body.trim()) { toast.error("Recipient and message are required"); return; }
    setSendingCompose(true);
    try {
      await api.sendEmail(composeForm);
      toast.success(`Email sent from ${composeForm.sender === "billing" ? "billing" : "hello"}@dondrae.com`);
      setShowCompose(false);
      setComposeForm({ to: "", subject: "", body: "", sender: "hello" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send the email");
    } finally { setSendingCompose(false); }
  };
  const createLeadFromMessage = async () => {
    const email = extractEmail(inboxMsg.from);
    const nameMatch = /^([^<]+)</.exec(inboxMsg.from || "");
    const name = (nameMatch ? nameMatch[1].trim() : "") || (email.split("@")[0] || "New Lead");
    if (!email || !email.includes("@")) { toast.error("No valid sender email to create a lead from"); return; }
    try {
      const res = await api.adminCreateBusiness({ business_name: name, contact_name: name, email, status: "lead" });
      toast.success(res.temp_password ? `Lead "${name}" created · temp login: ${res.temp_password}` : `Lead "${name}" created`, { duration: 9000 });
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create lead");
    }
  };
  const openMessage = async (mid) => {
    try { const m = await api.getInboxMessage(mid); setInboxMsg(m); loadInbox(); }
    catch { toast.error("Could not open message"); }
  };
  const deleteMessage = async (mid) => {
    if (!window.confirm("Delete this message?")) return;
    await api.deleteInboxMessage(mid);
    setInboxMsg(null); loadInbox(); toast.success("Message deleted");
  };
  const sendInboxReply = async () => {
    if (!inboxReply.trim()) return;
    setSendingReply(true);
    try {
      const updated = await api.replyInbox(inboxMsg.id, { body: inboxReply.trim() });
      setInboxMsg(updated);
      setInboxReply("");
      toast.success("Reply sent from hello@dondrae.com");
      loadInbox();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send reply");
    } finally { setSendingReply(false); }
  };

  const reloadProposals = async () => {
    try { setProposals(await api.listProposals()); } catch { /* ignore */ }
  };

  const onProposalSaved = async (saved) => {
    if (builderMode === "engagement" && saved?.id) {
      try { await api.adminAcceptProposal(saved.id); toast.success("Engagement created — invoice + workroom are ready"); }
      catch { toast.error("Saved, but could not open the workroom automatically"); }
    }
    setBuilderMode(null);
    await reloadProposals();
    if (detail) reloadDetail();
  };

  const copyProposalLink = (pid) => {
    const url = `${window.location.origin}/proposal/${pid}`;
    navigator.clipboard?.writeText(url).then(
      () => toast.success("Shareable link copied to clipboard"),
      () => toast.error("Could not copy link"),
    );
  };

  const removeProposal = async (pid) => {
    await api.deleteProposal(pid);
    setProposals((ps) => ps.filter((x) => x.id !== pid));
    toast.success("Proposal deleted");
  };

  const duplicateProposal = async (pid) => {
    try {
      const dup = await api.duplicateProposal(pid);
      await reloadProposals();
      toast.success("Proposal duplicated — opening for edit");
      setEditingProposal(dup);
      setShowBuilder(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not duplicate proposal");
    }
  };

  useEffect(() => { if (profile?.role === "admin") loadAll(); }, [profile]);

  const openBusiness = async (id) => {
    const d = await api.adminBusinessDetail(id);
    setDetail(d);
    setMgrForm(d.manager || { name: "", role: "Growth Strategist", photo: "" });
  };

  const createBusiness = async () => {
    if (!bizForm.business_name.trim() || !bizForm.email.trim()) { toast.error("Business name and email are required"); return; }
    setCreatingBiz(true);
    try {
      const res = await api.adminCreateBusiness(bizForm);
      const list = await api.adminBusinesses();
      setBusinesses(list);
      setShowAddBiz(false);
      setBizForm({ business_name: "", contact_name: "", email: "", phone: "", industry: "", address: "", status: "active" });
      if (res.temp_password) toast.success(`Business added. Temp login password: ${res.temp_password}`, { duration: 10000 });
      else toast.success("Business added");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add business");
    } finally { setCreatingBiz(false); }
  };

  const deleteBusiness = async (id, name) => {
    if (!window.confirm(`Delete "${name}"? This permanently removes the business and all its workflows, invoices, proposals, and messages.`)) return;
    try {
      await api.adminDeleteBusiness(id);
      setBusinesses((bs) => bs.filter((b) => b.id !== id));
      toast.success("Business deleted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete business");
    }
  };

  const reloadDetail = async () => {
    if (!detail) return;
    const d = await api.adminBusinessDetail(detail.profile.id);
    setDetail(d);
  };

  const startBizEdit = () => {
    const p = detail.profile;
    setBizEdit({
      business_name: p.business_name || "", industry: p.industry || "", contact_name: p.contact_name || "",
      email: p.email || "", phone: p.phone || "", website: p.website || "", address: p.address || "",
      company_size: p.company_size || "", status: p.status || "active", tags: (p.tags || []).join(", "), notes: p.notes || "",
    });
  };

  const saveBiz = async () => {
    const payload = { ...bizEdit, tags: bizEdit.tags.split(",").map((t) => t.trim()).filter(Boolean) };
    const updated = await api.adminUpdateBusiness(detail.profile.id, payload);
    setDetail((d) => ({ ...d, profile: { ...d.profile, ...updated } }));
    setBizEdit(null);
    toast.success("Business updated");
  };

  const advance = async (wid) => {
    const wf = await api.advanceWorkflow(wid);
    setDetail((d) => ({ ...d, workflows: d.workflows.map((w) => (w.id === wid ? wf : w)) }));
    toast.success(`Advanced to ${wf.stages[wf.current_stage_index]}`);
  };

  const assignManager = async () => {
    if (!mgrForm.name) { toast.error("Manager name is required"); return; }
    const m = await api.adminAssignManager(detail.profile.id, mgrForm);
    setDetail((d) => ({ ...d, manager: m }));
    toast.success(`${m.name} assigned as account manager`);
  };

  const sendReply = async (e) => {
    e.preventDefault();
    if (!reply.trim()) return;
    const msg = await api.sendMessage(detail.profile.id, "manager", reply.trim());
    setDetail((d) => ({ ...d, messages: [...d.messages, msg] }));
    setReply("");
  };

  const createLead = async (e) => {
    e.preventDefault();
    if (!leadForm.name) { toast.error("Lead name is required"); return; }
    const lead = await api.adminCreateLead(detail.profile.id, { ...leadForm, price: Number(leadForm.price) });
    setDetail((d) => ({ ...d, leads: [...d.leads, lead] }));
    setShowLead(false);
    setLeadForm({ name: "", business: "", email: "", phone: "", source: "DONDRÁE Sourced", price: 45 });
    toast.success("Lead offer sent to client");
  };

  const openWorkspace = async (w) => {
    const data = await api.getWorkspace(w.id);
    setWsPanel(data);
    api.getInvoice(w.id).then(setWsInvoice).catch(() => setWsInvoice(null));
  };

  const openWorkspaceById = async (wid) => {
    if (!wid) { toast.error("No workspace yet — approve or record a payment first."); return; }
    await openWorkspace({ id: wid });
  };

  const openDealWorkroom = async (pr) => {
    try {
      const res = await api.ensureProposalWorkspace(pr.id);
      const wid = res.workflow_id || pr.workflow_id;
      if (!wid) { toast.error("Could not open the workroom."); return; }
      await openWorkspace({ id: wid });
      reloadProposals();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open the workroom.");
    }
  };

  const isDeal = (pr) => pr.fully_paid || ["accepted", "deposit_paid", "paid"].includes(pr.status);
  const openProposals = proposals.filter((pr) => !isDeal(pr));
  const dealProposals = proposals.filter(isDeal);

  const sendWsMsg = async (e) => {
    e.preventDefault();
    if (!wsMsg.trim()) return;
    const msg = await api.postWorkspaceMessage(wsPanel.workflow.id, "agent", wsMsg.trim());
    setWsPanel((s) => ({ ...s, messages: [...s.messages, msg] }));
    setWsMsg("");
  };

  const advanceWs = async () => {
    const wf = await api.advanceWorkflow(wsPanel.workflow.id);
    setWsPanel((s) => ({ ...s, workflow: wf }));
    setDetail((d) => ({ ...d, workflows: d.workflows.map((w) => (w.id === wf.id ? wf : w)) }));
    toast.success(`Advanced to ${wf.stages[wf.current_stage_index]}`);
  };

  const addDeliv = async (e) => {
    e.preventDefault();
    if (!delivForm.title) { toast.error("Title is required"); return; }
    const d = await api.addDeliverable(wsPanel.workflow.id, delivForm);
    setWsPanel((s) => ({ ...s, deliverables: [d, ...s.deliverables] }));
    setDelivForm({ title: "", url: "", note: "" });
    toast.success("Deliverable shared with client");
  };

  const addItem = async (e) => {
    e.preventDefault();
    if (!itemForm.description || !itemForm.amount) { toast.error("Description and amount required"); return; }
    const amt = itemForm.credit ? -Math.abs(Number(itemForm.amount)) : Number(itemForm.amount);
    const inv = await api.addInvoiceItem(wsPanel.workflow.id, { description: itemForm.description, amount: amt });
    setWsInvoice(inv);
    setItemForm({ description: "", amount: "", credit: false });
    toast.success(itemForm.credit ? "Discount / credit applied" : "Line item added to invoice");
  };

  const removeItem = async (itemId) => {
    const inv = await api.deleteInvoiceItem(wsPanel.workflow.id, itemId);
    setWsInvoice(inv);
  };

  const applyPlan = async () => {
    try {
      const inv = await api.setInvoicePlan(wsPanel.workflow.id, Number(planCount));
      setWsInvoice(inv);
      toast.success(`Payment plan set: ${planCount} installments`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not set plan");
    }
  };

  const saveService = async (slug) => {
    const changes = editing[slug];
    if (!changes) return;
    const updated = await api.adminUpdateService(slug, { price: Number(changes.price), billing: changes.billing });
    setServices((sv) => sv.map((s) => (s.slug === slug ? updated : s)));
    setEditing((e) => { const n = { ...e }; delete n[slug]; return n; });
    toast.success(`${updated.title} pricing updated`);
  };

  if (loading || !profile) return <div className="min-h-screen clean-bg flex items-center justify-center text-zinc-500">Loading…</div>;

  return (
    <div className="min-h-screen clean-bg text-white">
      {/* Top bar */}
      <div className="glass-nav border-b border-white/10 px-4 sm:px-6 py-4 flex items-center justify-between sticky top-0 z-40">
        <div className="flex items-center gap-3">
          <div className="text-xl font-extrabold font-display">DONDR<span className="text-primary-400">Á</span>E</div>
          <span className="px-2 py-0.5 rounded bg-primary-500/10 text-primary-400 border border-primary-500/30 text-[10px] font-bold uppercase tracking-wider">Admin</span>
        </div>
        <button onClick={() => { logout(); navigate("/admin"); }} className="text-zinc-400 hover:text-red-400 flex items-center text-sm font-medium transition-colors">
          <LogOut size={16} className="mr-2" /> Sign out
        </button>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-10 space-y-8 fade-in">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-display">Admin Dashboard</h1>
          <p className="text-sm text-zinc-400 mt-1">Manage all client businesses, requests, and service catalog.</p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard icon={Building2} label="Businesses" value={stats?.businesses ?? 0} color="bg-blue-500/10 text-blue-400 border-blue-500/20" />
          <StatCard icon={FileText} label="Service Requests" value={stats?.requests ?? 0} color="bg-purple-500/10 text-purple-400 border-purple-500/20" />
          <StatCard icon={Layers} label="Active Services" value={stats?.active_services ?? 0} color="bg-green-500/10 text-green-400 border-green-500/20" />
          <StatCard icon={Activity} label="In-Progress" value={stats?.in_progress_workflows ?? 0} color="bg-orange-500/10 text-orange-400 border-orange-500/20" />
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-white/10 overflow-x-auto hide-scrollbar">
          {[["businesses", "Businesses"], ["contacts", "Contacts"], ["prospecting", "Prospecting"], ["requests", "Service Requests"], ["proposals", "Proposals"], ["workspaces", "Workspaces"], ["inbox", "Inbox"], ["services", "Manage Services"]].map(([k, l]) => (
            <button key={k} data-testid={`admin-tab-${k}`} onClick={() => { setTab(k); setDetail(null); if (k === "inbox") loadInbox(); }} className={`flex-shrink-0 whitespace-nowrap px-4 sm:px-5 py-3 text-sm font-bold border-b-2 transition-colors flex items-center gap-2 ${tab === k ? "border-primary-500 text-white" : "border-transparent text-zinc-500 hover:text-white"}`}>{l}{k === "inbox" && inboxUnread > 0 && <span className="px-1.5 py-0.5 rounded-full bg-primary-600 text-white text-[10px] font-bold" data-testid="inbox-unread-badge">{inboxUnread}</span>}</button>
          ))}
        </div>

        {/* Businesses tab */}
        {tab === "businesses" && !detail && (
          <div className="space-y-4">
            <div className="flex justify-end">
              <button onClick={() => setShowAddBiz(true)} className="px-4 py-2.5 bg-primary-600 text-white rounded-xl text-sm font-bold hover:bg-primary-500 transition flex items-center" data-testid="add-business-btn"><Plus size={15} className="mr-2" /> Add Business</button>
            </div>
            <div className="glass-panel rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="bg-white/5 text-zinc-400 uppercase text-xs font-bold tracking-wider border-b border-white/10">
                  <tr><th className="px-6 py-4">Business</th><th className="px-6 py-4">Contact</th><th className="px-6 py-4">Services</th><th className="px-6 py-4">Leads</th><th className="px-6 py-4">Requests</th><th className="px-6 py-4">Wallet</th><th className="px-6 py-4">Outstanding</th><th className="px-6 py-4 text-right">Actions</th></tr>
                </thead>
                <tbody className="divide-y divide-white/5 bg-black/20">
                  {businesses.length === 0 && (<tr><td colSpan={8} className="px-6 py-12 text-center text-zinc-500">No businesses yet. Click “Add Business” to create one.</td></tr>)}
                  {businesses.map((b) => (
                    <tr key={b.id} className="hover:bg-white/5 transition-colors cursor-pointer" onClick={() => openBusiness(b.id)} data-testid="business-row">
                      <td className="px-6 py-4"><div className="font-bold text-white flex items-center gap-2">{b.business_name}<span className={`px-1.5 py-0.5 rounded text-[9px] font-bold border uppercase ${BIZ_STATUS[b.status] || BIZ_STATUS.active}`}>{b.status || "active"}</span></div><div className="text-xs text-zinc-500">{b.industry}</div></td>
                      <td className="px-6 py-4"><div className="text-zinc-300">{b.contact_name}</div><div className="text-xs text-zinc-500">{b.email}</div></td>
                      <td className="px-6 py-4 text-zinc-300">{b.active_services}</td>
                      <td className="px-6 py-4 text-zinc-300">{b.leads}</td>
                      <td className="px-6 py-4 text-zinc-300">{b.requests}</td>
                      <td className="px-6 py-4 text-green-400 font-bold">${(b.balance || 0).toLocaleString()}</td>
                      <td className="px-6 py-4 font-bold">{(b.outstanding || 0) > 0 ? <span className="text-orange-400">${b.outstanding.toLocaleString()}</span> : <span className="text-zinc-600">—</span>}</td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          <button onClick={(e) => { e.stopPropagation(); openBusiness(b.id); }} title="Open" className="p-2 rounded-lg bg-white/5 text-zinc-300 hover:bg-white/10 hover:text-white transition"><ArrowRight size={14} /></button>
                          <button onClick={(e) => { e.stopPropagation(); deleteBusiness(b.id, b.business_name); }} title="Delete business" className="p-2 rounded-lg bg-white/5 text-zinc-300 hover:bg-red-500/20 hover:text-red-400 transition" data-testid="delete-business-btn"><Trash2 size={14} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            </div>
          </div>
        )}

        {/* Business detail */}
        {detail && (
          <div className="space-y-6">
            <button onClick={() => setDetail(null)} className="text-zinc-400 hover:text-white flex items-center text-sm font-medium"><ArrowLeft size={16} className="mr-2" /> Back to businesses</button>

            {/* Business info (editable) */}
            <div className="glass-panel rounded-2xl p-6" data-testid="business-info-card">
              {!bizEdit ? (
                <div>
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-3 flex-wrap">
                        <h2 className="text-2xl font-bold text-white">{detail.profile.business_name}</h2>
                        <span className={`px-2 py-0.5 rounded text-[10px] font-bold border uppercase ${BIZ_STATUS[detail.profile.status] || BIZ_STATUS.active}`}>{detail.profile.status || "active"}</span>
                      </div>
                      <p className="text-sm text-zinc-400 mt-1">{detail.profile.industry}{detail.profile.company_size ? ` · ${detail.profile.company_size}` : ""}</p>
                    </div>
                    <button onClick={startBizEdit} className="px-3 py-2 rounded-lg bg-white/5 text-zinc-200 text-sm font-bold hover:bg-white/10 flex items-center flex-shrink-0" data-testid="edit-business-btn"><Pencil size={13} className="mr-1.5" /> Edit</button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-2 mt-4 text-sm">
                    {detail.profile.email && <div className="flex items-center gap-2 text-zinc-300"><Mail size={13} className="text-zinc-500" /> {detail.profile.email}</div>}
                    {detail.profile.phone && <div className="flex items-center gap-2 text-zinc-300"><Phone size={13} className="text-zinc-500" /> {detail.profile.phone}</div>}
                    {detail.profile.website && <div className="flex items-center gap-2 text-zinc-300"><Globe size={13} className="text-zinc-500" /> {detail.profile.website}</div>}
                    {detail.profile.address && <div className="flex items-center gap-2 text-zinc-300"><MapPin size={13} className="text-zinc-500" /> {detail.profile.address}</div>}
                  </div>
                  {(detail.profile.tags || []).length > 0 && (
                    <div className="flex items-center gap-2 flex-wrap mt-3">{detail.profile.tags.map((t) => <span key={t} className="px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-xs text-zinc-300 flex items-center gap-1"><Tag size={10} /> {t}</span>)}</div>
                  )}
                  {detail.profile.notes && <p className="mt-3 text-sm text-zinc-400 bg-black/20 rounded-lg p-3 border border-white/10 whitespace-pre-wrap">{detail.profile.notes}</p>}
                </div>
              ) : (
                <div className="space-y-3" data-testid="business-edit-form">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div><label className="block text-[10px] font-bold text-zinc-500 uppercase tracking-wider mb-1.5">Business Name</label><input value={bizEdit.business_name} onChange={(e) => setBizEdit({ ...bizEdit, business_name: e.target.value })} className="w-full px-3 py-2.5 rounded-lg dark-input text-sm" data-testid="biz-name-input" /></div>
                    <div><label className="block text-[10px] font-bold text-zinc-500 uppercase tracking-wider mb-1.5">Industry</label><input value={bizEdit.industry} onChange={(e) => setBizEdit({ ...bizEdit, industry: e.target.value })} className="w-full px-3 py-2.5 rounded-lg dark-input text-sm" /></div>
                    <div><label className="block text-[10px] font-bold text-zinc-500 uppercase tracking-wider mb-1.5">Status</label>
                      <select value={bizEdit.status} onChange={(e) => setBizEdit({ ...bizEdit, status: e.target.value })} className="w-full px-3 py-2.5 rounded-lg dark-input text-sm" data-testid="biz-status-select">
                        <option value="lead">Lead</option><option value="active">Active</option><option value="paused">Paused</option><option value="churned">Churned</option>
                      </select>
                    </div>
                    <div><label className="block text-[10px] font-bold text-zinc-500 uppercase tracking-wider mb-1.5">Email</label><input value={bizEdit.email} onChange={(e) => setBizEdit({ ...bizEdit, email: e.target.value })} className="w-full px-3 py-2.5 rounded-lg dark-input text-sm" /></div>
                    <div><label className="block text-[10px] font-bold text-zinc-500 uppercase tracking-wider mb-1.5">Phone</label><input value={bizEdit.phone} onChange={(e) => setBizEdit({ ...bizEdit, phone: e.target.value })} className="w-full px-3 py-2.5 rounded-lg dark-input text-sm" /></div>
                    <div><label className="block text-[10px] font-bold text-zinc-500 uppercase tracking-wider mb-1.5">Website</label><input value={bizEdit.website} onChange={(e) => setBizEdit({ ...bizEdit, website: e.target.value })} placeholder="https://…" className="w-full px-3 py-2.5 rounded-lg dark-input text-sm" /></div>
                    <div className="md:col-span-2"><label className="block text-[10px] font-bold text-zinc-500 uppercase tracking-wider mb-1.5">Address</label><input value={bizEdit.address} onChange={(e) => setBizEdit({ ...bizEdit, address: e.target.value })} className="w-full px-3 py-2.5 rounded-lg dark-input text-sm" /></div>
                    <div><label className="block text-[10px] font-bold text-zinc-500 uppercase tracking-wider mb-1.5">Company Size</label><input value={bizEdit.company_size} onChange={(e) => setBizEdit({ ...bizEdit, company_size: e.target.value })} placeholder="e.g. 1-10" className="w-full px-3 py-2.5 rounded-lg dark-input text-sm" /></div>
                  </div>
                  <div><label className="block text-[10px] font-bold text-zinc-500 uppercase tracking-wider mb-1.5">Tags (comma separated)</label><input value={bizEdit.tags} onChange={(e) => setBizEdit({ ...bizEdit, tags: e.target.value })} placeholder="VIP, Events, Retainer" className="w-full px-3 py-2.5 rounded-lg dark-input text-sm" /></div>
                  <div><label className="block text-[10px] font-bold text-zinc-500 uppercase tracking-wider mb-1.5">Notes</label><textarea value={bizEdit.notes} onChange={(e) => setBizEdit({ ...bizEdit, notes: e.target.value })} rows={2} className="w-full px-3 py-2.5 rounded-lg dark-input text-sm" /></div>
                  <div className="flex justify-end gap-2">
                    <button onClick={() => setBizEdit(null)} className="px-4 py-2.5 rounded-lg bg-white/5 text-zinc-300 text-sm font-bold hover:bg-white/10">Cancel</button>
                    <button onClick={saveBiz} className="px-5 py-2.5 rounded-lg bg-primary-600 text-white text-sm font-bold hover:bg-primary-500 flex items-center" data-testid="biz-save-btn"><Save size={14} className="mr-1.5" /> Save</button>
                  </div>
                </div>
              )}
            </div>

            {/* Contacts */}
            <div className="glass-panel rounded-2xl p-6" data-testid="contacts-card">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2"><Users size={16} className="text-primary-400" /><h3 className="font-bold text-white text-sm">Contacts ({(detail.profile.contacts || []).length})</h3></div>
                <button onClick={() => setContactModal({ contact: null })} className="px-3 py-2 rounded-lg bg-primary-600 text-white text-xs font-bold hover:bg-primary-500 flex items-center" data-testid="add-contact-btn"><Plus size={13} className="mr-1" /> Add Contact</button>
              </div>
              {(detail.profile.contacts || []).length === 0 ? (
                <p className="text-zinc-500 text-sm">No contacts yet. Add the people you work with at this business.</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {(detail.profile.contacts || []).map((ct) => (
                    <button key={ct.id} onClick={() => setContactModal({ contact: ct })} className="text-left p-4 rounded-xl bg-black/30 border border-white/10 hover:border-primary-500/40 hover:bg-white/5 transition group" data-testid="contact-card">
                      <div className="flex items-start justify-between">
                        <div className="w-9 h-9 rounded-full bg-primary-500/15 text-primary-300 flex items-center justify-center font-bold text-sm">{(ct.name || "?").slice(0, 1).toUpperCase()}</div>
                        {ct.is_primary && <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-400 border border-amber-500/20 font-bold flex items-center gap-0.5"><Star size={9} /> PRIMARY</span>}
                      </div>
                      <div className="mt-2 font-bold text-white text-sm truncate">{ct.name}</div>
                      {ct.title && <div className="text-xs text-zinc-500 truncate">{ct.title}</div>}
                      <div className="mt-2 space-y-1">
                        {ct.emails?.[0]?.value && <div className="text-xs text-zinc-400 truncate flex items-center gap-1"><Mail size={10} /> {ct.emails[0].value}</div>}
                        {ct.phones?.[0]?.value && <div className="text-xs text-zinc-400 truncate flex items-center gap-1"><Phone size={10} /> {ct.phones[0].value}</div>}
                      </div>
                      <div className="mt-2 text-[10px] text-primary-400 font-bold opacity-0 group-hover:opacity-100">Manage →</div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Assign manager */}
            <div className="glass-panel rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-4"><UserCog size={16} className="text-blue-400" /><h3 className="font-bold text-white text-sm">Account Manager</h3></div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
                <div><label className="block text-[10px] font-bold text-zinc-500 uppercase tracking-wider mb-1.5">Name</label><input value={mgrForm?.name || ""} onChange={(e) => setMgrForm({ ...mgrForm, name: e.target.value })} className="w-full px-4 py-2.5 rounded-lg dark-input text-sm" placeholder="Sarah Jenkins" /></div>
                <div><label className="block text-[10px] font-bold text-zinc-500 uppercase tracking-wider mb-1.5">Role</label><input value={mgrForm?.role || ""} onChange={(e) => setMgrForm({ ...mgrForm, role: e.target.value })} className="w-full px-4 py-2.5 rounded-lg dark-input text-sm" placeholder="Growth Strategist" /></div>
                <div><label className="block text-[10px] font-bold text-zinc-500 uppercase tracking-wider mb-1.5">Photo URL</label><input value={mgrForm?.photo || ""} onChange={(e) => setMgrForm({ ...mgrForm, photo: e.target.value })} className="w-full px-4 py-2.5 rounded-lg dark-input text-sm" placeholder="https://…" /></div>
                <button onClick={assignManager} className="px-4 py-2.5 bg-primary-600 text-white rounded-lg text-sm font-bold hover:bg-primary-500 transition flex items-center justify-center"><Save size={14} className="mr-1.5" /> Assign</button>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="glass-panel rounded-2xl overflow-hidden">
                <div className="px-6 py-4 border-b border-white/10 bg-white/5 flex items-center gap-2"><FileText size={16} className="text-purple-400" /><h3 className="font-bold text-white text-sm">Service Requests ({detail.service_requests.length})</h3></div>
                <div className="divide-y divide-white/5 bg-black/20 max-h-72 overflow-y-auto hide-scrollbar">
                  {detail.service_requests.length === 0 && <div className="px-6 py-8 text-center text-zinc-500 text-sm">No requests</div>}
                  {detail.service_requests.map((r) => (
                    <div key={r.id} className="px-6 py-4">
                      <div className="flex justify-between items-center mb-2"><span className="font-bold text-white text-sm capitalize">{r.service_slug.replace(/-/g, " ")}</span><span className="px-2 py-0.5 rounded text-[10px] font-bold bg-blue-500/10 text-blue-400 border border-blue-500/20">{r.status}</span></div>
                      <div className="text-xs text-zinc-500 space-y-0.5">
                        {Object.entries(r.form_data || {}).map(([k, v]) => (<div key={k}><span className="text-zinc-600">{k.replace(/_/g, " ")}:</span> {String(v)}</div>))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="glass-panel rounded-2xl overflow-hidden">
                <div className="px-6 py-4 border-b border-white/10 bg-white/5 flex items-center justify-between">
                  <div className="flex items-center gap-2"><Users size={16} className="text-blue-400" /><h3 className="font-bold text-white text-sm">Leads ({detail.leads.length})</h3></div>
                  <button onClick={() => setShowLead(true)} className="px-3 py-1.5 bg-primary-600 text-white rounded-lg text-xs font-bold hover:bg-primary-500 transition flex items-center"><Plus size={12} className="mr-1" /> Offer Lead</button>
                </div>
                <div className="divide-y divide-white/5 bg-black/20 max-h-72 overflow-y-auto hide-scrollbar">
                  {detail.leads.length === 0 && <div className="px-6 py-8 text-center text-zinc-500 text-sm">No leads</div>}
                  {detail.leads.map((l) => (
                    <div key={l.id} className="px-6 py-3 flex justify-between items-center">
                      <div><div className="text-white text-sm font-medium">{l.name}</div><div className="text-xs text-zinc-500">{l.business}</div></div>
                      <div className="text-right">
                        {l.price ? <div className="text-sm font-bold text-white">${l.price}</div> : null}
                        <span className={`text-[10px] font-bold uppercase ${l.offer_status === "offered" ? "text-orange-400" : l.offer_status === "declined" ? "text-red-400" : "text-green-400"}`}>{l.offer_status || l.status}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Message thread with client */}
            <div className="glass-panel rounded-2xl overflow-hidden flex flex-col" style={{ maxHeight: "420px" }}>
              <div className="px-6 py-4 border-b border-white/10 bg-white/5 flex items-center gap-2"><Send size={15} className="text-blue-400" /><h3 className="font-bold text-white text-sm">Messages with {detail.profile.contact_name}</h3></div>
              <div className="flex-1 overflow-y-auto p-5 space-y-3 hide-scrollbar bg-black/20 min-h-[160px]">
                {detail.messages.length === 0 && <p className="text-center text-zinc-500 text-sm mt-6">No messages yet. Start the conversation below.</p>}
                {detail.messages.map((m) => (
                  <div key={m.id} className={`flex ${m.sender === "manager" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[75%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${m.sender === "manager" ? "bg-primary-600 text-white rounded-br-sm" : "bg-white/5 border border-white/10 text-zinc-200 rounded-bl-sm"}`}>{m.text}</div>
                  </div>
                ))}
              </div>
              <form onSubmit={sendReply} className="p-4 border-t border-white/10 flex gap-2">
                <input value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Reply as account manager…" className="flex-1 px-4 py-3 rounded-xl dark-input text-sm" />
                <button type="submit" disabled={!reply.trim()} className="w-12 h-12 flex items-center justify-center bg-primary-600 text-white rounded-xl hover:bg-primary-500 transition disabled:opacity-40"><Send size={18} /></button>
              </form>
            </div>

            <div className="glass-panel rounded-2xl p-6">
              <h3 className="font-bold text-white mb-6 flex items-center gap-2"><Activity size={16} className="text-orange-400" /> Workspaces</h3>
              {detail.workflows.length === 0 && <p className="text-zinc-500 text-sm">No open or past workspaces for this client yet.</p>}
              <div className="space-y-6">
                {detail.workflows.map((w) => {
                  const Icon = getServiceIcon(w.icon); const accent = getAccent(w.accent);
                  const done = w.current_stage_index >= w.stages.length - 1;
                  return (
                    <div key={w.id} className="p-5 rounded-xl bg-black/30 border border-white/5">
                      <div className="flex items-center gap-3 mb-4">
                        <div className={`w-10 h-10 rounded-lg border flex items-center justify-center ${accent.bg} ${accent.text} ${accent.border}`}><Icon size={18} /></div>
                        <div className="flex-1"><div className="font-bold text-white text-sm">{w.title}</div><div className="text-xs text-zinc-500">{done ? "Completed" : `Stage: ${w.stages[w.current_stage_index]}`}</div></div>
                        <button onClick={() => openWorkspace(w)} className="px-3 py-1.5 bg-white/10 text-white rounded-lg text-xs font-bold hover:bg-white/20 transition flex items-center mr-2"><MessagesSquare size={12} className="mr-1.5" /> Workspace</button>
                        {!done && <button onClick={() => advance(w.id)} className="px-3 py-1.5 bg-primary-600 text-white rounded-lg text-xs font-bold hover:bg-primary-500 transition flex items-center">Advance <ArrowRight size={12} className="ml-1.5" /></button>}
                      </div>
                      <div className="flex items-center">
                        {w.stages.map((st, i) => (
                          <React.Fragment key={st}>
                            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold border-2 flex-shrink-0 ${i < w.current_stage_index ? "bg-green-500/20 border-green-500 text-green-400" : i === w.current_stage_index ? "bg-primary-600 border-primary-400 text-white" : "bg-white/5 border-white/10 text-zinc-500"}`}>{i < w.current_stage_index ? <Check size={12} /> : i + 1}</div>
                            {i < w.stages.length - 1 && <div className={`flex-1 h-0.5 mx-1 ${i < w.current_stage_index ? "bg-green-500/50" : "bg-white/10"}`} />}
                          </React.Fragment>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Proposals & Invoices */}
            <div className="glass-panel rounded-2xl p-6" data-testid="client-proposals-section">
              <h3 className="font-bold text-white mb-5 flex items-center gap-2"><FileSignature size={16} className="text-primary-400" /> Proposals &amp; Invoices</h3>
              {(detail.proposals || []).length === 0 && (detail.invoices || []).length === 0 && (
                <p className="text-zinc-500 text-sm">No proposals or invoices for this client yet.</p>
              )}
              <div className="space-y-2">
                {(detail.proposals || []).map((pr) => (
                  <div key={pr.id} className="flex items-center gap-3 p-3 rounded-xl bg-black/30 border border-white/5" data-testid="client-proposal-row">
                    <FileSignature size={15} className="text-primary-400 flex-shrink-0" />
                    <button onClick={() => window.open(`/proposal/${pr.id}`, "_blank")} className="flex-1 text-left text-sm font-bold text-white hover:text-primary-300 transition truncate">{pr.title}</button>
                    <span className="text-xs text-zinc-400 whitespace-nowrap">${(pr.total || 0).toLocaleString()}{(pr.amount_paid || 0) > 0 && <span className="text-green-400"> · ${pr.amount_paid.toLocaleString()} paid</span>}{pr.balance > 0 && <span className="text-orange-400"> · ${pr.balance.toLocaleString()} due</span>}</span>
                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold border uppercase whitespace-nowrap ${pr.fully_paid || pr.status === "paid" ? "bg-green-500/10 text-green-400 border-green-500/20" : pr.status === "deposit_paid" ? "bg-teal-500/10 text-teal-400 border-teal-500/20" : pr.status === "accepted" ? "bg-green-500/10 text-green-400 border-green-500/20" : pr.status === "sent" ? "bg-blue-500/10 text-blue-400 border-blue-500/20" : "bg-white/5 text-zinc-400 border-white/10"}`}>{pr.fully_paid ? "paid" : (pr.status || "").replace("_", " ")}</span>
                    <button onClick={() => { setEditingProposal(pr); setBuilderMode(null); setShowBuilder(true); }} title="Edit proposal" className="p-2 rounded-lg bg-white/5 text-zinc-300 hover:bg-white/10 hover:text-white transition" data-testid="client-proposal-edit"><Pencil size={14} /></button>
                    <button onClick={() => setPayModal(pr)} title="Manage / record payment" className="p-2 rounded-lg bg-white/5 text-zinc-300 hover:bg-green-500/20 hover:text-green-400 transition" data-testid="client-proposal-manage"><DollarSign size={14} /></button>
                  </div>
                ))}
                {(detail.invoices || []).map((inv) => (
                  <div key={inv.workflow_id} className="flex items-center gap-3 p-3 rounded-xl bg-black/30 border border-white/5">
                    <Wallet size={15} className="text-blue-400 flex-shrink-0" />
                    <span className="flex-1 text-sm text-zinc-200 truncate">{inv.workflow_title || "Invoice"}</span>
                    <span className="text-xs text-zinc-400 whitespace-nowrap">${(inv.total || 0).toLocaleString()}{inv.balance > 0 && <span className="text-orange-400"> · ${inv.balance.toLocaleString()} due</span>}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
        {tab === "contacts" && !detail && <ContactsManager />}

        {tab === "prospecting" && !detail && <ProspectingAdmin />}

        {tab === "requests" && (
          <div className="glass-panel rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="bg-white/5 text-zinc-400 uppercase text-xs font-bold tracking-wider border-b border-white/10">
                  <tr><th className="px-6 py-4">Business</th><th className="px-6 py-4">Service</th><th className="px-6 py-4">Details</th><th className="px-6 py-4">Status</th></tr>
                </thead>
                <tbody className="divide-y divide-white/5 bg-black/20">
                  {requests.length === 0 && (<tr><td colSpan={4} className="px-6 py-12 text-center text-zinc-500">No service requests yet.</td></tr>)}
                  {requests.map((r) => (
                    <tr key={r.id} className="hover:bg-white/5">
                      <td className="px-6 py-4"><div className="font-bold text-white">{r.business_name}</div><div className="text-xs text-zinc-500">{r.contact_name}</div></td>
                      <td className="px-6 py-4 text-zinc-300 capitalize">{r.service_slug.replace(/-/g, " ")}</td>
                      <td className="px-6 py-4 text-xs text-zinc-400 max-w-xs">{Object.entries(r.form_data || {}).slice(0, 3).map(([k, v]) => `${k}: ${v}`).join(" · ")}</td>
                      <td className="px-6 py-4"><span className="px-2 py-0.5 rounded text-[10px] font-bold bg-blue-500/10 text-blue-400 border border-blue-500/20">{r.status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Proposals tab */}
        {tab === "proposals" && !detail && (
          <div className="space-y-4" data-testid="admin-proposals-tab">
            <div className="flex items-center justify-between">
              <p className="text-sm text-zinc-400">Create polished proposals and share a link clients can accept and attach to their profile.</p>
              <button onClick={() => { setEditingProposal(null); setShowBuilder(true); }} className="px-4 py-2.5 bg-primary-600 text-white rounded-xl text-sm font-bold hover:bg-primary-500 transition flex items-center" data-testid="new-proposal-btn"><Plus size={15} className="mr-1.5" /> New Proposal</button>
            </div>
            <div className="glass-panel rounded-2xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead className="bg-white/5 text-zinc-400 uppercase text-xs font-bold tracking-wider border-b border-white/10">
                    <tr><th className="px-6 py-4">Proposal</th><th className="px-6 py-4">Client</th><th className="px-6 py-4">Total</th><th className="px-6 py-4">Status</th><th className="px-6 py-4 text-right">Actions</th></tr>
                  </thead>
                  <tbody className="divide-y divide-white/5 bg-black/20">
                    {openProposals.length === 0 && (<tr><td colSpan={5} className="px-6 py-12 text-center text-zinc-500">No open proposals. Approved & paid ones move to the Workspaces tab.</td></tr>)}
                    {openProposals.map((pr) => (
                      <tr key={pr.id} className="hover:bg-white/5 transition-colors" data-testid="proposal-row">
                        <td className="px-6 py-4"><button onClick={() => window.open(`/proposal/${pr.id}`, "_blank")} className="font-bold text-white flex items-center gap-2 hover:text-primary-300 transition-colors text-left" data-testid="proposal-name-link"><FileSignature size={14} className="text-primary-400" /> <span className="underline decoration-dotted underline-offset-2">{pr.title}</span></button></td>
                        <td className="px-6 py-4 text-zinc-300">{pr.client_name || "—"}</td>
                        <td className="px-6 py-4 text-white font-bold">${(pr.total || 0).toLocaleString()}{(pr.amount_paid || 0) > 0 && <div className="text-[11px] text-green-400 font-bold">${(pr.amount_paid || 0).toLocaleString()} paid{!pr.fully_paid && ` · $${(pr.balance || 0).toLocaleString()} due`}</div>}</td>
                        <td className="px-6 py-4"><span className={`px-2 py-0.5 rounded text-[10px] font-bold border uppercase ${pr.fully_paid || pr.status === "paid" ? "bg-green-500/10 text-green-400 border-green-500/20" : pr.status === "deposit_paid" ? "bg-teal-500/10 text-teal-400 border-teal-500/20" : pr.status === "accepted" ? "bg-green-500/10 text-green-400 border-green-500/20" : pr.status === "sent" ? "bg-blue-500/10 text-blue-400 border-blue-500/20" : "bg-white/5 text-zinc-400 border-white/10"}`}>{pr.fully_paid ? "paid" : (pr.status || "").replace("_", " ")}</span></td>
                        <td className="px-6 py-4">
                          <div className="flex items-center justify-end gap-1.5">
                            <button title="Record payment / mark accepted" onClick={() => setPayModal(pr)} className="p-2 rounded-lg bg-white/5 text-zinc-300 hover:bg-green-500/20 hover:text-green-400 transition" data-testid="manage-proposal-payment"><DollarSign size={14} /></button>
                            <button title="Copy share link" onClick={() => copyProposalLink(pr.id)} className="p-2 rounded-lg bg-white/5 text-zinc-300 hover:bg-white/10 hover:text-white transition" data-testid="copy-proposal-link"><Copy size={14} /></button>
                            <button title="Duplicate" onClick={() => duplicateProposal(pr.id)} className="p-2 rounded-lg bg-white/5 text-zinc-300 hover:bg-white/10 hover:text-white transition" data-testid="duplicate-proposal-btn"><CopyPlus size={14} /></button>
                            <a title="Download PDF" href={api.proposalPdfUrl(pr.id)} target="_blank" rel="noopener noreferrer" className="p-2 rounded-lg bg-white/5 text-zinc-300 hover:bg-white/10 hover:text-white transition" data-testid="download-proposal-pdf"><Download size={14} /></a>
                            <button title="Edit" onClick={() => { setEditingProposal(pr); setShowBuilder(true); }} className="p-2 rounded-lg bg-white/5 text-zinc-300 hover:bg-white/10 hover:text-white transition"><Pencil size={14} /></button>
                            <button title="Delete" onClick={() => removeProposal(pr.id)} className="p-2 rounded-lg bg-white/5 text-zinc-300 hover:bg-red-500/20 hover:text-red-400 transition"><Trash2 size={14} /></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Workspaces tab (invoices + workrooms — accepted/paid deals) */}
        {tab === "workspaces" && !detail && (
          <div className="space-y-4" data-testid="admin-workspaces-tab">
            <div className="flex items-center justify-between gap-4">
              <p className="text-sm text-zinc-400">Active engagements — every approved or paid proposal becomes an invoice with a workroom here.</p>
              <button onClick={() => { setEditingProposal(null); setBuilderMode("engagement"); setShowBuilder(true); }} className="px-4 py-2.5 bg-primary-600 text-white rounded-xl text-sm font-bold hover:bg-primary-500 transition flex items-center flex-shrink-0" data-testid="new-engagement-btn"><Plus size={15} className="mr-1.5" /> New Engagement</button>
            </div>
            <div className="glass-panel rounded-2xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead className="bg-white/5 text-zinc-400 uppercase text-xs font-bold tracking-wider border-b border-white/10">
                    <tr><th className="px-6 py-4">Engagement</th><th className="px-6 py-4">Client</th><th className="px-6 py-4">Invoice</th><th className="px-6 py-4">Status</th><th className="px-6 py-4 text-right">Actions</th></tr>
                  </thead>
                  <tbody className="divide-y divide-white/5 bg-black/20">
                    {dealProposals.length === 0 && (<tr><td colSpan={5} className="px-6 py-12 text-center text-zinc-500">No active engagements yet. Approve or record a payment on a proposal to start one.</td></tr>)}
                    {dealProposals.map((pr) => (
                      <tr key={pr.id} className="hover:bg-white/5 transition-colors" data-testid="workspace-deal-row">
                        <td className="px-6 py-4 font-bold text-white">{pr.title}</td>
                        <td className="px-6 py-4 text-zinc-300">{pr.client_name || "—"}</td>
                        <td className="px-6 py-4 text-white font-bold">${(pr.total || 0).toLocaleString()}<div className="text-[11px] font-bold">{pr.fully_paid ? <span className="text-green-400">paid in full</span> : (pr.amount_paid > 0 ? <span className="text-teal-400">${pr.amount_paid.toLocaleString()} paid · ${pr.balance.toLocaleString()} due</span> : <span className="text-orange-400">${pr.balance.toLocaleString()} due</span>)}</div></td>
                        <td className="px-6 py-4"><span className={`px-2 py-0.5 rounded text-[10px] font-bold border uppercase ${pr.fully_paid || pr.status === "paid" ? "bg-green-500/10 text-green-400 border-green-500/20" : pr.status === "deposit_paid" ? "bg-teal-500/10 text-teal-400 border-teal-500/20" : "bg-green-500/10 text-green-400 border-green-500/20"}`}>{pr.fully_paid ? "paid" : (pr.status || "").replace("_", " ")}</span></td>
                        <td className="px-6 py-4">
                          <div className="flex items-center justify-end gap-1.5">
                            <button title="Open workroom" onClick={() => openDealWorkroom(pr)} className="px-3 py-1.5 bg-primary-600 text-white rounded-lg text-xs font-bold hover:bg-primary-500 transition flex items-center" data-testid="open-workspace-btn"><MessagesSquare size={13} className="mr-1.5" /> Workroom</button>
                            <button title="Invoice / payments" onClick={() => setPayModal(pr)} className="p-2 rounded-lg bg-white/5 text-zinc-300 hover:bg-green-500/20 hover:text-green-400 transition" data-testid="deal-manage-payment"><DollarSign size={14} /></button>
                            <button title="Edit" onClick={() => { setEditingProposal(pr); setShowBuilder(true); }} className="p-2 rounded-lg bg-white/5 text-zinc-300 hover:bg-white/10 hover:text-white transition"><Pencil size={14} /></button>
                            <a title="Download PDF" href={api.proposalPdfUrl(pr.id)} target="_blank" rel="noopener noreferrer" className="p-2 rounded-lg bg-white/5 text-zinc-300 hover:bg-white/10 hover:text-white transition"><Download size={14} /></a>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Inbox tab */}
        {tab === "inbox" && !detail && (
          <div className="space-y-4" data-testid="admin-inbox-tab">
            <div className="flex items-center justify-between gap-4">
              <p className="text-sm text-zinc-400">Emails sent to <span className="text-zinc-200 font-bold">dondrae@dondrae.com</span> and <span className="text-zinc-200 font-bold">outreach@dondrae.com</span> land here.</p>
              <div className="flex items-center gap-2">
                <button onClick={() => { setComposeForm({ to: "", subject: "", body: "", sender: "hello" }); setShowCompose(true); }} className="px-4 py-2 rounded-xl bg-primary-600 text-white text-sm font-bold hover:bg-primary-500 transition flex items-center" data-testid="inbox-compose-btn"><PenSquare size={14} className="mr-2" /> Compose</button>
                <button onClick={refreshInbox} disabled={refreshingInbox} className="px-3 py-2 rounded-xl bg-white/5 text-zinc-300 text-sm font-bold hover:bg-white/10 transition flex items-center disabled:opacity-60" data-testid="inbox-refresh">{refreshingInbox ? <Loader2 size={14} className="mr-2 animate-spin" /> : <RefreshCw size={14} className="mr-2" />} Refresh</button>
              </div>
            </div>
            <div className="glass-panel rounded-2xl overflow-hidden">
              {inbox.length === 0 && <div className="px-6 py-16 text-center text-zinc-500"><Inbox size={32} className="mx-auto mb-3 opacity-50" /><p>No messages yet. New emails to dondrae@dondrae.com will appear here.</p></div>}
              <div className="divide-y divide-white/5">
                {inbox.map((m) => (
                  <button key={m.id} onClick={() => openMessage(m.id)} className={`w-full text-left px-6 py-4 hover:bg-white/5 transition flex items-start gap-3 ${!m.read ? "bg-primary-500/5" : ""}`} data-testid="inbox-row">
                    <div className={`mt-1.5 w-2 h-2 rounded-full flex-shrink-0 ${m.read ? "bg-transparent border border-white/10" : "bg-primary-500"}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-3">
                        <span className={`text-sm truncate ${m.read ? "text-zinc-300" : "text-white font-bold"}`}>{m.from}</span>
                        <span className="text-[11px] text-zinc-500 flex-shrink-0">{(m.received_at || "").slice(0, 16).replace("T", " ")}</span>
                      </div>
                      <div className={`text-sm truncate ${m.read ? "text-zinc-500" : "text-zinc-200 font-medium"}`}>{m.subject}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Manage Services tab */}
        {tab === "services" && !detail && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {services.map((s) => {
              const Icon = getServiceIcon(s.icon); const accent = getAccent(s.accent);
              const edit = editing[s.slug];
              return (
                <div key={s.slug} className="glass-panel rounded-2xl p-6">
                  <div className="flex items-center gap-4 mb-5">
                    <div className={`w-12 h-12 rounded-xl border flex items-center justify-center ${accent.bg} ${accent.text} ${accent.border}`}><Icon size={22} /></div>
                    <div className="flex-1"><h3 className="font-bold text-white">{s.title}</h3><p className="text-xs text-zinc-500">{s.category}</p></div>
                  </div>
                  <p className="text-sm text-zinc-400 mb-5 line-clamp-2">{s.short_desc}</p>
                  <div className="flex items-end gap-3">
                    <div className="flex-1">
                      <label className="block text-[10px] font-bold text-zinc-500 uppercase tracking-wider mb-1.5">Price ($)</label>
                      <input type="number" value={edit ? edit.price : s.price} onChange={(e) => setEditing((ed) => ({ ...ed, [s.slug]: { price: e.target.value, billing: (ed[s.slug]?.billing) ?? s.billing } }))} className="w-full px-4 py-2.5 rounded-lg dark-input text-sm" />
                    </div>
                    <div className="flex-1">
                      <label className="block text-[10px] font-bold text-zinc-500 uppercase tracking-wider mb-1.5">Billing</label>
                      <input type="text" value={edit ? edit.billing : s.billing} onChange={(e) => setEditing((ed) => ({ ...ed, [s.slug]: { billing: e.target.value, price: (ed[s.slug]?.price) ?? s.price } }))} className="w-full px-4 py-2.5 rounded-lg dark-input text-sm" />
                    </div>
                    <button onClick={() => saveService(s.slug)} disabled={!edit} className="px-4 py-2.5 bg-primary-600 text-white rounded-lg text-sm font-bold hover:bg-primary-500 transition disabled:opacity-40 flex items-center">
                      {edit ? <><Save size={14} className="mr-1.5" /> Save</> : <><Pencil size={14} className="mr-1.5" /> Edit</>}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {inboxMsg && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/80 backdrop-blur-md p-4" data-testid="inbox-detail-modal">
          <div className="glass-panel bg-[#0a0a0b]/95 rounded-3xl shadow-2xl w-full max-w-2xl relative fade-in border border-white/10 max-h-[85vh] flex flex-col">
            <div className="px-6 py-4 border-b border-white/10 flex justify-between items-start bg-white/5 gap-4">
              <div className="min-w-0">
                <h3 className="text-base font-bold text-white truncate">{inboxMsg.subject}</h3>
                <p className="text-xs text-zinc-400 mt-0.5 truncate"><span className="text-zinc-500">From:</span> {inboxMsg.from}</p>
                {(inboxMsg.to || []).length > 0 && <p className="text-[11px] text-zinc-500 truncate"><span className="text-zinc-600">To:</span> {(inboxMsg.to || []).map((t) => (typeof t === "string" ? t : (t.address || t.email || ""))).join(", ")}</p>}
                <p className="text-[11px] text-zinc-500">{(inboxMsg.received_at || "").replace("T", " ").slice(0, 19)}</p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button onClick={createLeadFromMessage} className="p-2 rounded-lg bg-white/5 text-zinc-300 hover:bg-green-500/20 hover:text-green-400 transition" title="Create lead from sender" data-testid="inbox-create-lead"><UserPlus size={16} /></button>
                <button onClick={() => deleteMessage(inboxMsg.id)} className="p-2 rounded-lg bg-white/5 text-zinc-300 hover:bg-red-500/20 hover:text-red-400 transition" title="Delete" data-testid="inbox-delete"><Trash2 size={16} /></button>
                <button onClick={() => { setInboxMsg(null); setInboxReply(""); }} className="text-zinc-500 hover:text-white" data-testid="inbox-close"><X /></button>
              </div>
            </div>
            <div className="p-6 overflow-y-auto hide-scrollbar">
              {inboxMsg.html ? (
                <iframe title="email-body" sandbox="" srcDoc={inboxMsg.html} className="w-full min-h-[320px] bg-white rounded-lg border border-white/10" />
              ) : (
                <pre className="whitespace-pre-wrap text-sm text-zinc-200 font-sans leading-relaxed">{inboxMsg.text || "(no content)"}</pre>
              )}
              {(inboxMsg.attachments || []).length > 0 && (
                <div className="mt-4 pt-4 border-t border-white/10 text-xs text-zinc-400 flex items-center gap-2"><Paperclip size={13} /> Attachments: {inboxMsg.attachments.map((a) => a.filename || a.name).join(", ")}</div>
              )}

              {(inboxMsg.replies || []).length > 0 && (
                <div className="mt-5 pt-5 border-t border-white/10 space-y-3" data-testid="inbox-replies">
                  <div className="text-[10px] font-bold text-zinc-500 uppercase tracking-wider">Your Replies</div>
                  {(inboxMsg.replies || []).map((rp, i) => (
                    <div key={i} className="rounded-xl bg-primary-500/10 border border-primary-500/20 p-3">
                      <div className="text-[11px] text-zinc-500 mb-1">Sent to {rp.to} · {(rp.at || "").replace("T", " ").slice(0, 16)} · from hello@dondrae.com</div>
                      <div className="text-sm text-zinc-100 whitespace-pre-wrap">{rp.body}</div>
                    </div>
                  ))}
                </div>
              )}

              <div className="mt-5 pt-5 border-t border-white/10">
                <label className="block text-[10px] font-bold text-zinc-500 uppercase tracking-wider mb-2">Reply as hello@dondrae.com</label>
                <textarea value={inboxReply} onChange={(e) => setInboxReply(e.target.value)} rows={4} placeholder={`Write a reply to ${extractEmail(inboxMsg.from)}…`} className="w-full px-4 py-3 rounded-xl dark-input text-sm" data-testid="inbox-reply-input" />
                <div className="flex justify-end mt-3">
                  <button onClick={sendInboxReply} disabled={sendingReply || !inboxReply.trim()} className="px-5 py-2.5 rounded-xl bg-primary-600 text-white text-sm font-bold hover:bg-primary-500 transition disabled:opacity-50 flex items-center" data-testid="inbox-reply-send">{sendingReply ? <Loader2 size={15} className="mr-2 animate-spin" /> : <Reply size={15} className="mr-2" />} Send Reply</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {showCompose && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/80 backdrop-blur-md p-4" data-testid="compose-modal">
          <div className="glass-panel bg-[#0a0a0b]/95 rounded-3xl shadow-2xl w-full max-w-xl relative fade-in border border-white/10">
            <div className="px-6 py-4 border-b border-white/10 flex justify-between items-center bg-white/5">
              <h3 className="text-base font-bold text-white flex items-center gap-2"><PenSquare size={17} className="text-primary-400" /> Compose Email</h3>
              <button onClick={() => setShowCompose(false)} className="text-zinc-500 hover:text-white"><X /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex items-center gap-3">
                <label className="text-[10px] font-bold text-zinc-500 uppercase tracking-wider w-14">From</label>
                <select value={composeForm.sender} onChange={(e) => setComposeForm({ ...composeForm, sender: e.target.value })} className="flex-1 px-3 py-2.5 rounded-lg dark-input text-sm" data-testid="compose-sender">
                  <option value="hello">hello@dondrae.com (general)</option>
                  <option value="billing">billing@dondrae.com (billing)</option>
                </select>
              </div>
              <div className="flex items-center gap-3">
                <label className="text-[10px] font-bold text-zinc-500 uppercase tracking-wider w-14">To</label>
                <input value={composeForm.to} onChange={(e) => setComposeForm({ ...composeForm, to: e.target.value })} placeholder="recipient@example.com" className="flex-1 px-3 py-2.5 rounded-lg dark-input text-sm" data-testid="compose-to" />
              </div>
              <div className="flex items-center gap-3">
                <label className="text-[10px] font-bold text-zinc-500 uppercase tracking-wider w-14">Subject</label>
                <input value={composeForm.subject} onChange={(e) => setComposeForm({ ...composeForm, subject: e.target.value })} placeholder="Subject" className="flex-1 px-3 py-2.5 rounded-lg dark-input text-sm" data-testid="compose-subject" />
              </div>
              <textarea value={composeForm.body} onChange={(e) => setComposeForm({ ...composeForm, body: e.target.value })} rows={7} placeholder="Write your message…" className="w-full px-4 py-3 rounded-xl dark-input text-sm" data-testid="compose-body" />
            </div>
            <div className="px-6 py-4 border-t border-white/10 flex justify-end gap-2 bg-white/5">
              <button onClick={() => setShowCompose(false)} className="px-5 py-2.5 rounded-xl bg-white/5 text-zinc-300 text-sm font-bold hover:bg-white/10 transition">Cancel</button>
              <button onClick={sendCompose} disabled={sendingCompose} className="px-5 py-2.5 rounded-xl bg-primary-600 text-white text-sm font-bold hover:bg-primary-500 transition disabled:opacity-60 flex items-center" data-testid="compose-send">{sendingCompose ? <Loader2 size={15} className="mr-2 animate-spin" /> : <Send size={15} className="mr-2" />} Send Email</button>
            </div>
          </div>
        </div>
      )}

      {showLead && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/80 backdrop-blur-md p-4">
          <div className="glass-panel bg-[#0a0a0b]/95 rounded-3xl shadow-2xl w-full max-w-md relative fade-in overflow-hidden border border-white/10">
            <div className="px-6 py-5 border-b border-white/10 flex justify-between items-center bg-white/5">
              <h3 className="text-lg font-bold text-white">Offer a Lead</h3>
              <button onClick={() => setShowLead(false)} className="text-zinc-500 hover:text-white"><X /></button>
            </div>
            <form onSubmit={createLead} className="p-8 space-y-4">
              <input value={leadForm.name} onChange={(e) => setLeadForm({ ...leadForm, name: e.target.value })} placeholder="Lead Name *" className="w-full px-5 py-3.5 rounded-xl dark-input" />
              <input value={leadForm.business} onChange={(e) => setLeadForm({ ...leadForm, business: e.target.value })} placeholder="Business Name" className="w-full px-5 py-3.5 rounded-xl dark-input" />
              <input value={leadForm.email} onChange={(e) => setLeadForm({ ...leadForm, email: e.target.value })} placeholder="Email" className="w-full px-5 py-3.5 rounded-xl dark-input" />
              <input value={leadForm.phone} onChange={(e) => setLeadForm({ ...leadForm, phone: e.target.value })} placeholder="Phone" className="w-full px-5 py-3.5 rounded-xl dark-input" />
              <div>
                <label className="block text-[10px] font-bold text-zinc-500 uppercase tracking-wider mb-1.5">Lead Price ($)</label>
                <input type="number" value={leadForm.price} onChange={(e) => setLeadForm({ ...leadForm, price: e.target.value })} className="w-full px-5 py-3.5 rounded-xl dark-input" />
              </div>
              <button type="submit" className="w-full py-3.5 bg-primary-600 text-white rounded-xl font-bold hover:bg-primary-500 transition-colors shadow-[0_0_15px_rgba(59,130,246,0.4)]">Send Offer to Client</button>
            </form>
          </div>
        </div>
      )}

      {wsPanel && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/80 backdrop-blur-md p-2 sm:p-4">
          <div className="glass-panel bg-[#0a0a0b]/95 rounded-2xl sm:rounded-3xl shadow-2xl w-full max-w-3xl relative fade-in overflow-hidden border border-white/10 flex flex-col" style={{ maxHeight: "94vh" }}>
            <div className="px-4 sm:px-6 py-4 border-b border-white/10 flex justify-between items-center bg-white/5">
              <div>
                <h3 className="text-base font-bold text-white">{wsPanel.workflow.title}</h3>
                <p className="text-xs text-zinc-500">Workspace · {wsPanel.client.business_name} · Stage: {wsPanel.workflow.stages[wsPanel.workflow.current_stage_index]}</p>
              </div>
              <button onClick={() => setWsPanel(null)} className="text-zinc-500 hover:text-white"><X /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-6 space-y-6 hide-scrollbar">
              {/* Stage control */}
              <div className="flex items-center gap-3">
                <div className="flex-1 flex items-center flex-wrap gap-1">
                  {wsPanel.workflow.stages.map((st, i) => (
                    <span key={st} className={`px-2.5 py-1 rounded-md text-[11px] font-bold ${i < wsPanel.workflow.current_stage_index ? "bg-green-500/15 text-green-400" : i === wsPanel.workflow.current_stage_index ? "bg-primary-600 text-white" : "bg-white/5 text-zinc-500"}`}>{st}</span>
                  ))}
                </div>
                {wsPanel.workflow.current_stage_index < wsPanel.workflow.stages.length - 1 && (
                  <button onClick={advanceWs} className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-bold hover:bg-primary-500 transition flex items-center flex-shrink-0">Advance <ArrowRight size={14} className="ml-1.5" /></button>
                )}
              </div>

              {/* Messages */}
              <div>
                <h4 className="text-sm font-bold text-white uppercase tracking-wider mb-3">Messages</h4>
                <div className="bg-black/30 border border-white/10 rounded-2xl p-4 space-y-3 max-h-52 overflow-y-auto hide-scrollbar mb-3">
                  {wsPanel.messages.length === 0 && <p className="text-center text-zinc-500 text-sm py-4">No messages yet.</p>}
                  {wsPanel.messages.map((m) => (
                    <div key={m.id} className={`flex ${m.sender === "agent" ? "justify-end" : "justify-start"}`}>
                      <div className="max-w-[78%]">
                        <div className={`text-[10px] mb-1 text-zinc-500 ${m.sender === "agent" ? "text-right" : ""}`}>{m.sender_name}</div>
                        <div className={`px-4 py-2.5 rounded-2xl text-sm whitespace-pre-wrap ${m.sender === "agent" ? "bg-primary-600 text-white rounded-br-sm" : "bg-white/5 border border-white/10 text-zinc-200 rounded-bl-sm"}`}>{m.text}</div>
                      </div>
                    </div>
                  ))}
                </div>
                <form onSubmit={sendWsMsg} className="flex gap-2">
                  <input value={wsMsg} onChange={(e) => setWsMsg(e.target.value)} placeholder="Reply to client as agent…" className="flex-1 px-4 py-3 rounded-xl dark-input text-sm" />
                  <button type="submit" disabled={!wsMsg.trim()} className="w-12 h-12 flex items-center justify-center bg-primary-600 text-white rounded-xl hover:bg-primary-500 transition disabled:opacity-40"><Send size={18} /></button>
                </form>
              </div>

              {/* Deliverables */}
              <div>
                <h4 className="text-sm font-bold text-white uppercase tracking-wider mb-3">Deliverables / Files</h4>
                <div className="space-y-2 mb-4">
                  {wsPanel.deliverables.length === 0 && <p className="text-zinc-500 text-sm">No files shared yet.</p>}
                  {wsPanel.deliverables.map((d) => (
                    <div key={d.id} className="flex items-center gap-3 p-3 rounded-xl bg-black/30 border border-white/10">
                      <Paperclip size={16} className="text-primary-400 flex-shrink-0" />
                      <div className="flex-1 min-w-0"><div className="text-white text-sm font-bold">{d.title}</div>{d.note && <div className="text-xs text-zinc-500">{d.note}</div>}</div>
                      {d.url && <a href={d.url} target="_blank" rel="noopener noreferrer" className="text-primary-400 hover:text-primary-300"><ExternalLink size={15} /></a>}
                    </div>
                  ))}
                </div>
                <form onSubmit={addDeliv} className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  <input value={delivForm.title} onChange={(e) => setDelivForm({ ...delivForm, title: e.target.value })} placeholder="Title *" className="px-4 py-2.5 rounded-lg dark-input text-sm" />
                  <input value={delivForm.url} onChange={(e) => setDelivForm({ ...delivForm, url: e.target.value })} placeholder="Link URL" className="px-4 py-2.5 rounded-lg dark-input text-sm" />
                  <div className="flex gap-2">
                    <input value={delivForm.note} onChange={(e) => setDelivForm({ ...delivForm, note: e.target.value })} placeholder="Note" className="flex-1 px-4 py-2.5 rounded-lg dark-input text-sm" />
                    <button type="submit" className="px-4 py-2.5 bg-white text-black rounded-lg text-sm font-bold hover:bg-zinc-200 transition flex items-center"><Plus size={14} /></button>
                  </div>
                </form>
              </div>

              {/* Event Hub modules */}
              <div>
                <h4 className="text-sm font-bold text-white uppercase tracking-wider mb-3">Event Hub — Delivery Modules</h4>
                <WorkspaceModules workflowId={wsPanel.workflow.id} workflow={wsPanel.workflow} by="admin" onChange={(updated) => setWsPanel((s) => ({ ...s, workflow: { ...s.workflow, ...updated } }))} />
              </div>

              {/* Financials / Invoice */}
              {wsInvoice && (
                <div>
                  <h4 className="text-sm font-bold text-white uppercase tracking-wider mb-3">Financials / Invoice</h4>
                  <div className="grid grid-cols-3 gap-2 mb-3">
                    <div className="p-3 rounded-xl bg-black/30 border border-white/10"><div className="text-[10px] text-zinc-500 uppercase font-bold">Total</div><div className="text-lg font-bold text-white">${wsInvoice.total.toLocaleString()}</div></div>
                    <div className="p-3 rounded-xl bg-black/30 border border-white/10"><div className="text-[10px] text-zinc-500 uppercase font-bold">Paid</div><div className="text-lg font-bold text-green-400">${wsInvoice.paid.toLocaleString()}</div></div>
                    <div className="p-3 rounded-xl bg-black/30 border border-white/10"><div className="text-[10px] text-zinc-500 uppercase font-bold">Balance</div><div className={`text-lg font-bold ${wsInvoice.balance > 0 ? "text-orange-400" : "text-white"}`}>${wsInvoice.balance.toLocaleString()}</div></div>
                  </div>
                  <div className="bg-black/30 border border-white/10 rounded-xl divide-y divide-white/5 mb-3">
                    {wsInvoice.items.map((it) => (
                      <div key={it.id} className="px-4 py-2.5 flex items-center justify-between text-sm">
                        <span className="text-zinc-200">{it.description} {it.amount < 0 ? <span className="text-[10px] text-teal-400 font-bold uppercase ml-2">Credit</span> : it.paid && <span className="text-[10px] text-green-400 font-bold uppercase ml-2">Paid</span>}</span>
                        <span className="flex items-center gap-3"><span className={`font-bold ${it.amount < 0 ? "text-teal-400" : "text-white"}`}>{it.amount < 0 ? `-$${Math.abs(it.amount).toLocaleString()}` : `$${it.amount.toLocaleString()}`}</span>{!it.paid && <button onClick={() => removeItem(it.id)} className="text-zinc-500 hover:text-red-400"><X size={14} /></button>}</span>
                      </div>
                    ))}
                  </div>
                  <form onSubmit={addItem} className="space-y-2 mb-3">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                      <input value={itemForm.description} onChange={(e) => setItemForm({ ...itemForm, description: e.target.value })} placeholder={itemForm.credit ? "Discount / credit description *" : "New item description *"} className="md:col-span-2 px-4 py-2.5 rounded-lg dark-input text-sm" />
                      <div className="flex gap-2">
                        <input type="number" value={itemForm.amount} onChange={(e) => setItemForm({ ...itemForm, amount: e.target.value })} placeholder="$ Amount" className="flex-1 px-4 py-2.5 rounded-lg dark-input text-sm" />
                        <button type="submit" className="px-4 py-2.5 bg-white text-black rounded-lg text-sm font-bold hover:bg-zinc-200 transition flex items-center"><Plus size={14} /></button>
                      </div>
                    </div>
                    <label className="flex items-center gap-2 text-xs text-zinc-400 cursor-pointer select-none">
                      <input type="checkbox" checked={itemForm.credit} onChange={(e) => setItemForm({ ...itemForm, credit: e.target.checked })} className="accent-teal-500" />
                      Apply as a discount / credit (subtracts from the invoice total)
                    </label>
                  </form>
                  <div className="flex items-center gap-2 p-3 rounded-xl bg-black/30 border border-white/10">
                    <span className="text-sm text-zinc-400">Split outstanding balance into</span>
                    <input type="number" min="1" max="12" value={planCount} onChange={(e) => setPlanCount(e.target.value)} className="w-16 px-3 py-2 rounded-lg dark-input text-sm text-center" />
                    <span className="text-sm text-zinc-400">installments</span>
                    <button onClick={applyPlan} className="ml-auto px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-bold hover:bg-primary-500 transition">Set Plan</button>
                  </div>
                  {wsInvoice.plan?.enabled && (
                    <div className="mt-3 space-y-1.5">
                      {wsInvoice.plan.installments.map((inst) => (
                        <div key={inst.id} className="flex justify-between text-xs px-3 py-2 rounded-lg bg-black/20 border border-white/5"><span className="text-zinc-300">{inst.label}</span><span className={inst.status === "paid" ? "text-green-400 font-bold" : "text-orange-400 font-bold"}>${inst.amount.toLocaleString()} · {inst.status}</span></div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showBuilder && (
        <ProposalBuilder
          proposal={editingProposal}
          services={services}
          engagementMode={builderMode === "engagement"}
          onClose={() => { setShowBuilder(false); setEditingProposal(null); setBuilderMode(null); }}
          onSaved={onProposalSaved}
        />
      )}
      {payModal && (
        <ProposalPaymentModal
          proposal={payModal}
          onClose={() => setPayModal(null)}
          onUpdated={(updated) => { setPayModal(updated); reloadProposals(); }}
        />
      )}

      {showAddBiz && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/80 backdrop-blur-md p-4" data-testid="add-business-modal">
          <div className="glass-panel bg-[#0a0a0b]/95 rounded-3xl shadow-2xl w-full max-w-lg relative fade-in border border-white/10">
            <div className="px-6 py-4 border-b border-white/10 flex justify-between items-center bg-white/5">
              <h3 className="text-base font-bold text-white flex items-center gap-2"><Building2 size={17} className="text-primary-400" /> Add Business</h3>
              <button onClick={() => setShowAddBiz(false)} className="text-zinc-500 hover:text-white"><X /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><label className="block text-[10px] font-bold text-zinc-500 uppercase tracking-wider mb-1.5">Business Name *</label><input value={bizForm.business_name} onChange={(e) => setBizForm({ ...bizForm, business_name: e.target.value })} className="w-full px-3 py-2.5 rounded-lg dark-input text-sm" data-testid="add-biz-name" /></div>
                <div><label className="block text-[10px] font-bold text-zinc-500 uppercase tracking-wider mb-1.5">Contact Name</label><input value={bizForm.contact_name} onChange={(e) => setBizForm({ ...bizForm, contact_name: e.target.value })} className="w-full px-3 py-2.5 rounded-lg dark-input text-sm" data-testid="add-biz-contact" /></div>
                <div><label className="block text-[10px] font-bold text-zinc-500 uppercase tracking-wider mb-1.5">Email *</label><input type="email" value={bizForm.email} onChange={(e) => setBizForm({ ...bizForm, email: e.target.value })} className="w-full px-3 py-2.5 rounded-lg dark-input text-sm" data-testid="add-biz-email" /></div>
                <div><label className="block text-[10px] font-bold text-zinc-500 uppercase tracking-wider mb-1.5">Phone</label><input value={bizForm.phone} onChange={(e) => setBizForm({ ...bizForm, phone: e.target.value })} className="w-full px-3 py-2.5 rounded-lg dark-input text-sm" /></div>
                <div><label className="block text-[10px] font-bold text-zinc-500 uppercase tracking-wider mb-1.5">Industry</label><input value={bizForm.industry} onChange={(e) => setBizForm({ ...bizForm, industry: e.target.value })} className="w-full px-3 py-2.5 rounded-lg dark-input text-sm" /></div>
                <div><label className="block text-[10px] font-bold text-zinc-500 uppercase tracking-wider mb-1.5">Status</label><select value={bizForm.status} onChange={(e) => setBizForm({ ...bizForm, status: e.target.value })} className="w-full px-3 py-2.5 rounded-lg dark-input text-sm"><option value="active">Active</option><option value="lead">Lead</option><option value="paused">Paused</option><option value="churned">Churned</option></select></div>
              </div>
              <div><label className="block text-[10px] font-bold text-zinc-500 uppercase tracking-wider mb-1.5">Address</label><input value={bizForm.address} onChange={(e) => setBizForm({ ...bizForm, address: e.target.value })} className="w-full px-3 py-2.5 rounded-lg dark-input text-sm" /></div>
              <p className="text-[11px] text-zinc-500">A client login will be created automatically; a temporary password is shown after you save.</p>
            </div>
            <div className="px-6 py-4 border-t border-white/10 flex justify-end gap-2 bg-white/5">
              <button onClick={() => setShowAddBiz(false)} className="px-5 py-2.5 rounded-xl bg-white/5 text-zinc-300 text-sm font-bold hover:bg-white/10 transition">Cancel</button>
              <button onClick={createBusiness} disabled={creatingBiz} className="px-5 py-2.5 rounded-xl bg-primary-600 text-white text-sm font-bold hover:bg-primary-500 transition disabled:opacity-60 flex items-center" data-testid="add-biz-submit">{creatingBiz ? <Loader2 size={15} className="mr-2 animate-spin" /> : <Plus size={15} className="mr-2" />} Create Business</button>
            </div>
          </div>
        </div>
      )}
      {contactModal && detail && (
        <ContactModal
          businessId={detail.profile.id}
          contact={contactModal.contact}
          activities={(detail.profile.activities || []).filter((a) => !contactModal.contact?.id || !a.contact_id || a.contact_id === contactModal.contact.id)}
          onSaved={reloadDetail}
          onActivitiesChange={reloadDetail}
          onClose={() => setContactModal(null)}
        />
      )}

      <Copilot
        context={
          wsPanel ? { page: "workroom", workspace_id: wsPanel.workflow?.id }
          : (showBuilder && editingProposal) ? { page: "proposal-builder", proposal_id: editingProposal.id }
          : detail ? { page: "business", business_id: detail.profile?.id }
          : { page: tab }
        }
        onApplied={async () => {
          loadAll();
          reloadProposals();
          if (detail) reloadDetail();
          if (wsPanel?.workflow?.id) { try { setWsPanel(await api.getWorkspace(wsPanel.workflow.id)); } catch { /* ignore */ } }
        }}
      />
    </div>
  );
}
